import axios, { type AxiosRequestConfig } from 'axios';

declare module 'axios' {
  export interface AxiosRequestConfig {
    /** Optional enrichment endpoints: 5xx must not trigger the global error page. */
    skipGlobalServerError?: boolean;
    /**
     * Switch User privileged calls: send the saved admin token on this request
     * only, without rewriting the impersonated session in storage.
     */
    useAdminSession?: boolean;
  }
}

const FALLBACK_LOCALE = 'en';

const AUTH_STORAGE_KEYS = [
  'access_token',
  'access_token_type',
  'client_profile_id',
  'recruiter_company_id',
  'isAdmin',
  'admin_user_id',
] as const;

const storageCache = new Map<string, string | null>();

const recruiterCompanyIdListeners = new Set<() => void>();

function getCachedStorageItem(key: string): string | null {
  if (!storageCache.has(key)) {
    storageCache.set(key, localStorage.getItem(key));
  }
  return storageCache.get(key) ?? null;
}

function notifyRecruiterCompanyIdListeners() {
  recruiterCompanyIdListeners.forEach((listener) => listener());
}

/** Same-tab subscription for workspace switcher UI (localStorage alone does not re-render). */
export function subscribeRecruiterCompanyId(listener: () => void): () => void {
  recruiterCompanyIdListeners.add(listener);
  return () => {
    recruiterCompanyIdListeners.delete(listener);
  };
}

/** Invalidate after auth/profile mutations (same-tab localStorage writes). */
export function invalidateAuthStorageCache(keys?: string[]) {
  if (!keys) {
    storageCache.clear();
    return;
  }
  keys.forEach((key) => storageCache.delete(key));
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (
      event.key &&
      AUTH_STORAGE_KEYS.includes(
        event.key as (typeof AUTH_STORAGE_KEYS)[number],
      )
    ) {
      storageCache.delete(event.key);
      if (event.key === 'recruiter_company_id') {
        notifyRecruiterCompanyIdListeners();
      }
    }
  });
}

/** Keep the API Accept-Language header in sync with the UI locale. */
export const syncAcceptLanguageHeader = (locale?: string | null) => {
  axios.defaults.headers.common['Accept-Language'] = locale || FALLBACK_LOCALE;
};

export function applyAuthHeaders(config: AxiosRequestConfig) {
  if (!config.headers) return;

  const adminToken =
    config.useAdminSession === true
      ? localStorage.getItem('admin_token')
      : null;
  const accessToken = adminToken || getCachedStorageItem('access_token');
  if (accessToken) {
    const tokenType = (
      getCachedStorageItem('access_token_type') || 'token'
    ).toLowerCase();
    const prefix = tokenType === 'bearer' ? 'Bearer' : 'Token';
    config.headers.Authorization = `${prefix} ${accessToken}`;
  } else {
    delete config.headers.Authorization;
  }

  if (adminToken) {
    delete config.headers['X-Client-Profile-Id'];
    delete config.headers['X-Login-As-User'];
    delete config.headers['X-Admin-User-Id'];
    return;
  }

  const clientProfileId = getCachedStorageItem('client_profile_id');
  if (clientProfileId) {
    config.headers['X-Client-Profile-Id'] = clientProfileId;
  } else {
    delete config.headers['X-Client-Profile-Id'];
  }

  const recruiterCompanyId = getCachedStorageItem('recruiter_company_id');
  if (recruiterCompanyId) {
    config.headers['X-Recruiter-Company-Id'] = recruiterCompanyId;
  } else {
    delete config.headers['X-Recruiter-Company-Id'];
  }

  const isLoginAsUser = getCachedStorageItem('isAdmin');
  const adminUserId = getCachedStorageItem('admin_user_id');
  if (isLoginAsUser === 'true' && adminUserId) {
    config.headers['X-Login-As-User'] = 'true';
    config.headers['X-Admin-User-Id'] = adminUserId;
  } else {
    delete config.headers['X-Login-As-User'];
    delete config.headers['X-Admin-User-Id'];
  }
}

axios.interceptors.request.use(
  (config) => {
    applyAuthHeaders(config);
    return config;
  },
  (error: unknown) =>
    Promise.reject(error instanceof Error ? error : new Error(String(error))),
);

export const setClientProfileId = (profileId: string | null) => {
  if (profileId) {
    localStorage.setItem('client_profile_id', profileId);
  } else {
    localStorage.removeItem('client_profile_id');
  }
  invalidateAuthStorageCache(['client_profile_id']);
};

export const getClientProfileId = (): string | null => {
  return getCachedStorageItem('client_profile_id');
};

export const setRecruiterCompanyId = (companyId: string | null) => {
  if (companyId) {
    localStorage.setItem('recruiter_company_id', companyId);
  } else {
    localStorage.removeItem('recruiter_company_id');
  }
  invalidateAuthStorageCache(['recruiter_company_id']);
  notifyRecruiterCompanyIdListeners();
};

export const getRecruiterCompanyId = (): string | null => {
  return getCachedStorageItem('recruiter_company_id');
};
